import React, { FC, useCallback, useContext, useMemo } from "react";
import IConfigurationContext from "../../../../../infrastructure/context/configuration/configuration.context.interface";
import ConfigurationContext from "../../../../../infrastructure/context/configuration/configuration.context";
import { Address } from "../../../../../infrastructure/http/modules/cart/cart.models";
import AddressInputField from "./address-input-field";
import useInputId from "../hooks/useInputId";
import TranslationManager from "../../../../../infrastructure/translation-manager";
import TranslationsContext from "../../../../../infrastructure/context/translations/translations-context";
import { TranslationKeys } from "../../../../../infrastructure/const";
import { AddressState } from "../checkout-address-form";
import CheckUtils from "../../../../../utilities/check-utils";
import CountrySelect from "./country-select";
import SaveAddressCheckbox from "../../save-address-checkbox";
import AdressevaelgerAutocomplete  from "./adressevaelger/adressevaelger-autocomplete";
import { FeatureFlagValues } from "../../../../../infrastructure/const";

type Props = {
  formFields: (keyof Address)[];
  requiredFields: (keyof Address)[];
  displayEmailAddress: boolean;
  idPrefix: string;
  displayValidationMessages: boolean;
  onInputValueChange: (
    name: keyof Address,
    value: string,
    isValid: boolean
  ) => void;
  onAddressApiValueChange: (
    address: Address
  ) => void;
  state: AddressState;
  saveAddress: boolean;
  onSaveAddressChanged: (saveAddressValue: boolean) => void;
};

const NewAddressForm: FC<Props> = ({
  formFields,
  displayEmailAddress,
  idPrefix,
  requiredFields,
  displayValidationMessages,
  onInputValueChange,
  onAddressApiValueChange,
  state,
  saveAddress,
  onSaveAddressChanged,
}) => {
  const configurationContext = useContext<IConfigurationContext>(ConfigurationContext);
  const translationsContext = useContext(TranslationsContext);
  const translationManager = new TranslationManager(translationsContext);
  const { getFeatureFlagValue } = configurationContext;

  const { generateInputId } = useInputId(idPrefix);

  const shouldRenderMiddleName = useMemo(
    () =>
      configurationContext &&
      !configurationContext.isFeatureFlagEnabled("hideCustomerMiddleName") &&
      formFields.includes("middleName" as keyof Address),
    [configurationContext, formFields]
  );

  const shouldRenderEmail = useMemo(
    () => displayEmailAddress && formFields.includes("email" as keyof Address),
    [formFields, displayEmailAddress]
  );

  const isRequired = useCallback(
    (fieldName: keyof Address): boolean => {
      const isRequired = requiredFields.includes(fieldName as keyof Address);
      return isRequired;
    },
    [requiredFields]
  );

  const hasApiError = useCallback(
    (fieldName: keyof Address): boolean => {
      return !CheckUtils.isNullOrEmptyArray(state.apiErrorResponse) &&
        state.apiErrorResponse.includes(fieldName.toLowerCase());
    },
    [state.apiErrorResponse]
  );

  const translations = useMemo<Record<keyof Address, string>>(
    () => ({
      firstName: translationManager.getTranslation(TranslationKeys.FIRST_NAME),
      lastName: translationManager.getTranslation(TranslationKeys.LAST_NAME),
      middleName: translationManager.getTranslation(
        TranslationKeys.MIDDLE_NAME
      ),
      email: translationManager.getTranslation(TranslationKeys.EMAIL),
      company: translationManager.getTranslation(TranslationKeys.COMPANY),
      addressApiText: translationManager.getTranslation(TranslationKeys.ADDRESSAPI_ADDRESS),
      addressLine1: translationManager.getTranslation(TranslationKeys.ADDRESS),
      addressLine2: translationManager.getTranslation(
        TranslationKeys.ADDRESS_LINE2
      ),
      postalCode: translationManager.getTranslation(TranslationKeys.POSTALCODE),
      city: translationManager.getTranslation(TranslationKeys.CITY),
      phoneNumber: translationManager.getTranslation(TranslationKeys.PHONE),
      countryCode: translationManager.getTranslation(TranslationKeys.COUNTRY),
      id: "",
      county: "",
    }),
    [translationManager]
  );

  return (
    <fieldset className="group-select" id="billing-new-address-form">
      <ul>
        {/* Always render core address fields in the right order */}
        {formFields.includes("firstName") && (
          <AddressInputField
            fieldName="firstName"
            id={generateInputId(translations.firstName)}
            label={translations.firstName}
            value={state.firstName}
            isRequired={isRequired("firstName")}
            hasError={hasApiError("firstName")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {shouldRenderMiddleName && (
          <AddressInputField
            fieldName="middleName"
            id={generateInputId(translations.middleName)}
            label={translations.middleName}
            value={state.middleName}
            isRequired={isRequired("middleName")}
            hasError={hasApiError("middleName")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("lastName" as keyof Address) && (
          <AddressInputField
            fieldName="lastName"
            id={generateInputId(translations.lastName)}
            label={translations.lastName}
            value={state.lastName}
            isRequired={isRequired("lastName")}
            hasError={hasApiError("lastName")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {shouldRenderEmail && (
          <AddressInputField
            fieldName="email"
            id={generateInputId(translations.email)}
            label={translations.email}
            value={state.email}
            isRequired={isRequired("email")}
            hasError={hasApiError("email")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("company" as keyof Address) && (
          <AddressInputField
            fieldName="company"
            id={generateInputId(translations.company)}
            label={translations.company}
            value={state.company}
            isRequired={isRequired("company")}
            hasError={hasApiError("company")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {getFeatureFlagValue("orderSummaryType") === FeatureFlagValues.OrderSummary.Battery && (
          <li className="input-container">
            <AdressevaelgerAutocomplete 
              label={translations.addressApiText}
              addressSearchText={state.addressApiText}
              onSelect={(address: Address)=> {onAddressApiValueChange(address)}} 
            />
          </li>
        )}

        {formFields.includes("addressLine1") && (
          <AddressInputField
            fieldName="addressLine1"
            id={generateInputId(translations.addressLine1)}
            label={translations.addressLine1}
            value={state.addressLine1}
            isRequired={isRequired("addressLine1")}
            hasError={hasApiError("addressLine1")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("addressLine2") && (
          <AddressInputField
            fieldName="addressLine2"
            id={generateInputId(translations.addressLine2)}
            label={translations.addressLine2 || ""}
            value={state.addressLine2}
            isRequired={isRequired("addressLine2")}
            hasError={hasApiError("addressLine2")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("postalCode") && (
          <AddressInputField
            fieldName="postalCode"
            id={generateInputId(translations.postalCode)}
            label={translations.postalCode}
            value={state.postalCode}
            isRequired={isRequired("postalCode")}
            hasError={hasApiError("postalCode")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("city") && (
          <AddressInputField
            fieldName="city"
            id={generateInputId(translations.city)}
            label={translations.city}
            value={state.city}
            isRequired={isRequired("city")}
            hasError={hasApiError("city")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("phoneNumber") && (
          <AddressInputField
            fieldName="phoneNumber"
            id={generateInputId(translations.phoneNumber)}
            label={translations.phoneNumber}
            value={state.phoneNumber}
            isRequired={isRequired("phoneNumber")}
            hasError={hasApiError("phoneNumber")}
            displayValidationMessages={displayValidationMessages}
            onInputValueChange={onInputValueChange}
          />
        )}

        {formFields.includes("countryCode" as keyof Address) && (
          <CountrySelect
            countryLabel={translations.countryCode}
            inputId={generateInputId(translations.countryCode)}
            onCountryChange={(countryCode) =>
              onInputValueChange("countryCode", countryCode, true)
            }
          />
        )}
      </ul>
      <SaveAddressCheckbox
        checked={saveAddress}
        onChange={onSaveAddressChanged}
        id={idPrefix + "save-checkbox"}
      />
    </fieldset>
  );
};

export default NewAddressForm;
